import { Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { socialReasonsText } from '@/lib/social';
import {
  SOCIAL_STATUS_COLOR,
  SOCIAL_STATUS_LABEL,
  brandLabel,
  isInsufficientState,
  socialIndexDisplay,
  type WeeklySocialRiskRow,
} from '@/lib/socialRisk';

/**
 * REDFOX 社交传播风险 brand-week 面板（PR3-C-2）。
 * honest：ok/partial 渲 index（partial 标「有限/部分数据」）；insufficient/no_run 渲「—」+ reasons，
 * **非 0/非绿/非低风险**。platform_breakdown 分平台；outlier 单列不进主分；nlp_unverified 可见。
 * **0** 绑定逐帖 raw count；**0** VOC/NPS。
 */
function platformSummary(row: WeeklySocialRiskRow): string {
  const entries = Object.entries(row.platform_breakdown);
  if (!entries.length) return '—';
  return entries.map(([p, b]) => `${p}: ${b.platform_signal.toFixed(2)}`).join(' / ');
}

const columns: ColumnsType<WeeklySocialRiskRow> = [
  {
    title: '品牌',
    dataIndex: 'brand_id',
    width: 150,
    render: (id: string) => (
      <span>
        <b>{brandLabel(id)}</b> <span className="text-[11px] text-slate-400">{id}</span>
      </span>
    ),
  },
  {
    title: '传播信号状态',
    dataIndex: 'signal_status',
    width: 130,
    render: (s: WeeklySocialRiskRow['signal_status']) => (
      <Tag color={SOCIAL_STATUS_COLOR[s]}>{SOCIAL_STATUS_LABEL[s]}</Tag>
    ),
  },
  {
    title: '传播分 (0-100)',
    key: 'index',
    width: 110,
    render: (_: unknown, row: WeeklySocialRiskRow) =>
      isInsufficientState(row.signal_status) ? (
        <span className="text-slate-400">{socialIndexDisplay(row)}</span> // 「—」非 0/绿
      ) : (
        <b>{socialIndexDisplay(row)}</b>
      ),
  },
  {
    title: '本周帖数 (mapped stored)',
    dataIndex: 'stored_post_count',
    width: 150,
  },
  {
    title: '平台分解',
    key: 'platforms',
    width: 200,
    render: (_: unknown, row: WeeklySocialRiskRow) => (
      <Tooltip title={`per-platform signal（独立归一，不跨平台混 raw）；${row.platform_count} 平台`}>
        <span className="text-[12px]">{platformSummary(row)}</span>
      </Tooltip>
    ),
  },
  {
    title: '高互动 outlier',
    dataIndex: 'high_engagement_outlier_count',
    width: 120,
    render: (n: number) => (
      <Tooltip title="高互动 outlier 帖数，单列、不计入传播分">
        <span>{n}</span>
      </Tooltip>
    ),
  },
  {
    title: '情绪',
    dataIndex: 'sentiment_status',
    width: 110,
    render: () => <Tag>未验证情绪</Tag>, // sentiment_status 恒 nlp_unverified（可见真值，非「负面情绪」）
  },
  {
    title: '信号说明',
    dataIndex: 'signal_reasons',
    width: 180,
    render: (reasons: string[]) => (
      <span className="text-[12px] text-slate-500">{socialReasonsText(reasons)}</span>
    ),
  },
];

export default function SocialRiskPanel({ rows }: { rows: WeeklySocialRiskRow[] }) {
  return (
    <Table<WeeklySocialRiskRow>
      rowKey="brand_id"
      dataSource={rows}
      columns={columns}
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
    />
  );
}
